import React, { useEffect } from 'react'
import { gql } from 'graphql-request'
import { useParams, useNavigate, Routes, Route, useLocation } from 'react-router-dom'
import {
  BotMessageSquare,
  Settings2,
  Copy
} from 'lucide-react'
import OverflowMenu from '@components/OverflowMenu'

import { useCurrentUser } from '@contexts/currentUser'
import { useChannel } from '@contexts/actionCable'
import { useQuery, useMutation } from '@hooks/graphql'
import Breadcrumbs from '@components/Breadcrumbs'
import { useFlashMessage } from '@components/FlashMessage'
import NavTabs from '@/components/NavTabs'

import TutorConversation from './TutorConversation'
import { useSubscription } from '../../hooks/subscription'
import AssignDialog from './AssignDialog'
import ShareDialog from './ShareDialog'
import Settings from './Settings'

import { Button } from '@components/ui/button'


const TUTOR_QUERY = gql`
  query tutor($id: ID!) {
    node(id: $id) {
      ... on Tutor {
        id
        name
        description
        summary
        socratic
        suggestionsEnabled
        stemModeEnabled
        knowledgeBaseEnabled
        visibility
        isOwner
        imageGenerationStatus
        imageGenerationStartedAt
        image {
          url
        }
        documents {
          id
          file {
            url
            filename
          }
        }
        userName
        organizationName
        tags {
          id
          name
        }
        model
      }
    }
  }
`

const CREATE_COPY_MUTATION = gql`
  mutation createCopyOfTutor($input: CreateCopyInput!) {
    createCopyOfTutor(input: $input) {
      tutor {
        id
      }
    }
  }
`

const TutorDetails = () => {
  const { id } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const { hasProFeatures } = useSubscription()
  const { setFlashMessage } = useFlashMessage()

  const previousListParams = location.state?.previousListParams || ''

  const { isLoading, refetch, data: { node: tutor = {} } = {} } = useQuery({
    queryKey: ['tutorDetails', id],
    gqlQuery: TUTOR_QUERY,
    variables: { id }
  })

  const { mutate: createCopy, isLoading: isCopying } = useMutation({
    gqlMutation: CREATE_COPY_MUTATION,
    variables: { input: { tutorId: id } },
    onSuccess: data => navigate(`/educators/tutors/${data.createCopyOfTutor.tutor.id}`)
  })

  const { currentMembership: { organization: { id: organizationId } = {} } = {} } = useCurrentUser()
  const { subscribe, unsubscribe } = useChannel()

  useEffect(() => {
    subscribe(
      {
        channel: 'Tutor::ImageGenerationChannel',
        tutor_id: id,
        organization_id: organizationId
      },
      {
        received: (data) => {
          // reload tutor data whenever image generation status changes
          if (data.type === 'image_generation_status') {
            refetch()
            if (data.status == 'failed') {
              setFlashMessage('Your image failed to generate. Please try again.', 'error')
            }
          }
        },
        connected: () => refetch()
      }
    )

    return () => unsubscribe()
  }, [id])

  if (isLoading) return

  const ActionButtonMenu = () => (
    <Choose>
      <When condition={tutor.isOwner}>
        <div className='ml-auto flex items-center h-full'>
          <AssignDialog
            tutorId={tutor.id}
            name={tutor.name}
            refetch={refetch}
          />

          <OverflowMenu orientation='vertical' className='ml-2'>
            <ShareDialog
              tutorId={tutor.id}
              name={tutor.name}
              summary={tutor.summary}
              visibility={tutor.visibility}
            />
            <OverflowMenu.Item>
              <button
                onClick={() => createCopy()}
                disabled={!hasProFeatures || isCopying}
                className={`flex gap-2 text-md text-left items-center whitespace-nowrap ${!hasProFeatures ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Copy className="h-4 w-4" />
                {isCopying ? "Copying..." : "Make a copy"}
                <If condition={!hasProFeatures}>
                  <span className="ml-2 inline-flex items-center">
                    (
                    <span className="text-xs text-gray-500 inline-flex items-center">
                      <img
                        className="mr-2 w-4 h-4"
                        src="/crown.svg"
                        alt="crown"
                      />
                      Pro
                    </span>
                    )
                  </span>
                </If>
              </button>
            </OverflowMenu.Item>
          </OverflowMenu>
        </div>
      </When>
      <Otherwise>
        <>
          {/* Non-owner view */}
          <div className='ml-auto flex items-center h-full'>
            {/* Desktop: show button directly, Mobile: show in overflow menu */}
            <div className="hidden md:flex">
              <Button
                onClick={() => createCopy()}
                disabled={!hasProFeatures || isCopying}
                className={`flex gap-2 text-md text-left items-center whitespace-nowrap ${!hasProFeatures ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Copy className="h-4 w-4" />
                {isCopying ? "Copying..." : "Make a copy"}
                <If condition={!hasProFeatures}>
                  <span className="ml-2 inline-flex items-center">
                    (
                    <span className="text-xs text-gray-500 inline-flex items-center">
                      <img
                        className="mr-2 w-4 h-4"
                        src="/crown.svg"
                        alt="crown"
                      />
                      Pro
                    </span>
                    )
                  </span>
                </If>
              </Button>
            </div>
            <div className="md:hidden">
              <OverflowMenu orientation='vertical' className='ml-2'>
                <OverflowMenu.Item
                  onClick={() => createCopy()} disabled={!hasProFeatures || isCopying}>
                   <button
                onClick={() => createCopy()}
                disabled={!hasProFeatures || isCopying}
                className={`flex gap-2 text-md text-left items-center whitespace-nowrap ${!hasProFeatures ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <Copy className="h-4 w-4" />
                {isCopying ? "Copying..." : "Make a copy"}
                <If condition={!hasProFeatures}>
                  <span className="ml-2 inline-flex items-center">
                    (
                    <span className="text-xs text-gray-500 inline-flex items-center">
                      <img
                        className="mr-2 w-4 h-4"
                        src="/crown.svg"
                        alt="crown"
                      />
                      Pro
                    </span>
                    )
                  </span>
                </If>
              </button>
                </OverflowMenu.Item>
              </OverflowMenu>
            </div>
          </div>
        </>
      </Otherwise>
    </Choose>
  )

  return (
    <div className='flex flex-col h-screen'>
      <Breadcrumbs>
        <Breadcrumbs.Link label='Tutors' to={`/educators/tutors${previousListParams}`} />
        <Breadcrumbs.Text label={tutor.name} />
        <ActionButtonMenu />
      </Breadcrumbs>

      <div className='h-[calc(100%-64px)]'>
        <NavTabs className='mx-5 justify-between'>
          <div className="h-full flex items-center">
            <NavTabs.Link
              to=''
              state={{ previousListParams }}
              end
              icon={<BotMessageSquare size={16} />}
              label='Chat'
            />
            <NavTabs.Link
              to='settings'
              state={{ previousListParams }}
              icon={<Settings2 size={16} />}
              label='Settings'
            />
          </div>
        </NavTabs>

        <div className='h-[calc(100%-51px)]'>
          <Routes>
            <Route
              index
              element={
                <TutorConversation
                  tutorId={tutor.id}
                  name={tutor.name}
                  imageGenerationStatus={tutor.imageGenerationStatus}
                  imageGenerationStartedAt={tutor.imageGenerationStartedAt}
                  imageUrl={tutor.image?.url}
                />
              }
            />

            <Route
              path='settings'
              element={
                <Settings
                  tutorId={tutor.id}
                  isOwner={tutor.isOwner}
                  name={tutor.name}
                  description={tutor.description}
                  summary={tutor.summary}
                  socratic={tutor.socratic}
                  stemModeEnabled={tutor.stemModeEnabled}
                  knowledgeBaseEnabled={tutor.knowledgeBaseEnabled}
                  suggestionsEnabled={tutor.suggestionsEnabled}
                  visibility={tutor.visibility}
                  refetch={refetch}
                  imageGenerationStatus={tutor.imageGenerationStatus}
                  imageGenerationStartedAt={tutor.imageGenerationStartedAt}
                  imageUrl={tutor.image?.url}
                  documents={tutor.documents}
                  tagIds={tutor.tags?.map(tag => tag.id)}
                  model={tutor.model}
                />
              }
            />
          </Routes>
        </div>
      </div>
    </div>
  )
}

export default TutorDetails
